import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Paper, CircularProgress, Stack,
  IconButton, Tooltip, ToggleButtonGroup, ToggleButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

// Scène-blokken → platte tekst voor de AI-breakdown (en als fallback-weergave).
const naarPlatteTekst = (blokken) => blokken.map((b, i) => {
  const delen = [`## Scène ${i + 1}${b.titel ? ` — ${b.titel}` : ''}`];
  if (b.vo?.trim()) delen.push(`VOICE-OVER/DIALOOG:\n${b.vo.trim()}`);
  if (b.beeld?.trim()) delen.push(`BEELD:\n${b.beeld.trim()}`);
  return delen.join('\n');
}).join('\n\n');

const leegBlok = () => ({ key: `b${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, titel: '', vo: '', beeld: '' });

// Platte tekst → blokken. Herkent het eigen serialisatie-formaat (## Scène-koppen
// met VOICE-OVER/BEELD-labels) zodat plat ⇄ blokken zonder verlies heen en weer
// kan; ander materiaal wordt per alinea (lege regel) een scène-blok.
const naarBlokken = (tekst) => {
  const schoon = String(tekst || '').trim();
  if (!schoon) return [leegBlok()];
  if (/^## /m.test(schoon)) {
    const delen = schoon.split(/^## /m).map((d) => d.trim()).filter(Boolean);
    const blokken = delen.map((deel, i) => {
      const [kopregel, ...rest] = deel.split('\n');
      const kopMatch = kopregel.match(/^Sc[eè]ne \d+\s*(?:—\s*(.*))?$/);
      const titel = kopMatch ? (kopMatch[1] || '') : kopregel.trim();
      const lichaam = rest.join('\n');
      const voMatch = lichaam.match(/VOICE-OVER\/DIALOOG:\n([\s\S]*?)(?=\nBEELD:|$)/);
      const beeldMatch = lichaam.match(/BEELD:\n([\s\S]*)$/);
      const vo = voMatch ? voMatch[1].trim() : (beeldMatch ? '' : lichaam.trim());
      return { key: `b${Date.now()}-${i}`, titel, vo, beeld: beeldMatch ? beeldMatch[1].trim() : '' };
    });
    if (blokken.length) return blokken;
  }
  return schoon.split(/\n\s*\n/).map((a) => a.trim()).filter(Boolean)
    .map((alinea, i) => ({ key: `b${Date.now()}-${i}`, titel: '', vo: alinea, beeld: '' }));
};


const ScriptInput = ({ project, onRefresh, onNext }) => {
  const [modus, setModus] = useState(() => {
    try { if (JSON.parse(project.script_json || 'null')?.length) return 'blokken'; } catch { /* plat */ }
    return project.script_text ? 'plat' : 'blokken';
  });
  const [blokken, setBlokken] = useState(() => {
    try {
      const b = JSON.parse(project.script_json || 'null');
      if (Array.isArray(b) && b.length) return b.map((x, i) => ({ key: x.key || `b${i}`, titel: x.titel || '', vo: x.vo || '', beeld: x.beeld || '' }));
    } catch { /* leeg starten */ }
    return [leegBlok()];
  });
  const [scriptText, setScriptText] = useState(project.script_text || '');
  const [projectNumber, setProjectNumber] = useState(project.project_number || '');
  const [generating, setGenerating] = useState(false);
  const [pdfBezig, setPdfBezig] = useState(false);
  const [opslag, setOpslag] = useState('idle'); // idle | bezig | opgeslagen | fout
  // ✨ inline herschrijf-hulp: { blokKey, veld, instructie, resultaat, bezig }
  const [aiDlg, setAiDlg] = useState(null);

  const saveScript = useCallback(async (huidigeBlokken, huidigeTekst, huidigModus) => {
    const inBlokModus = huidigModus === 'blokken';
    const platte = inBlokModus ? naarPlatteTekst(huidigeBlokken) : huidigeTekst;
    const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${project.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: project.title,
        script_text: platte,
        // In blok-modus zijn de blokken de bron; in plat-modus laten we
        // eerder bewaarde blokken staan (null = niet aanraken op de server).
        script_json: inBlokModus ? JSON.stringify(huidigeBlokken) : null,
        project_number: projectNumber.trim(),
      }),
    });
    if (!resp.ok) throw new Error('Opslaan mislukt');
  }, [project.id, project.title, projectNumber]);

  // Autosave met debounce — de editor voelt als een document, niet als een formulier.
  // Saves lopen door één ketting (geen out-of-order overschrijven) en de laatste
  // wijziging wordt bij unmount alsnog weggeschreven (Codex ronde 12 #5).
  const actueelRef = useRef({ blokken, scriptText, modus });
  useEffect(() => { actueelRef.current = { blokken, scriptText, modus }; });
  const saveKetting = useRef(Promise.resolve());
  const pendingRef = useRef(false);
  const flushSave = useCallback(() => {
    const p = saveKetting.current.then(() => {
      const { blokken: b, scriptText: t, modus: m } = actueelRef.current;
      return saveScript(b, t, m);
    });
    saveKetting.current = p.catch(() => {});
    return p;
  }, [saveScript]);
  const eersteRender = useRef(true);
  useEffect(() => {
    if (eersteRender.current) { eersteRender.current = false; return; }
    setOpslag('bezig');
    pendingRef.current = true;
    const timer = setTimeout(async () => {
      pendingRef.current = false;
      try { await flushSave(); setOpslag('opgeslagen'); }
      catch { setOpslag('fout'); }
    }, 1200);
    return () => clearTimeout(timer);
  }, [blokken, scriptText, projectNumber, modus, flushSave]);
  // Unmount met nog niet weggeschreven wijziging → direct wegschrijven.
  useEffect(() => () => { if (pendingRef.current) flushSave().catch(() => {}); }, [flushSave]);

  const wijzigBlok = (key, veld, waarde) =>
    setBlokken((prev) => prev.map((b) => (b.key === key ? { ...b, [veld]: waarde } : b)));
  const verplaatsBlok = (idx, richting) => setBlokken((prev) => {
    const doel = idx + richting;
    if (doel < 0 || doel >= prev.length) return prev;
    const kopie = [...prev];
    [kopie[idx], kopie[doel]] = [kopie[doel], kopie[idx]];
    return kopie;
  });
  const verwijderBlok = (key) => setBlokken((prev) => (prev.length > 1 ? prev.filter((b) => b.key !== key) : prev));

  const wisselModus = (_e, nieuw) => {
    if (!nieuw || nieuw === modus) return;
    if (nieuw === 'blokken') {
      // De platte tekst is leidend als die is bewerkt sinds de laatste
      // serialisatie — anders raakte je platte wijzigingen kwijt (Codex #4).
      const platNu = scriptText.trim();
      if (platNu && platNu !== naarPlatteTekst(blokken).trim()) {
        setBlokken(naarBlokken(scriptText));
      }
    } else {
      const heeftBlokInhoud = blokken.some((b) => b.vo?.trim() || b.beeld?.trim());
      if (heeftBlokInhoud) setScriptText(naarPlatteTekst(blokken));
    }
    setModus(nieuw);
  };

  const runHerschrijf = async () => {
    if (!aiDlg) return;
    const blok = blokken.find((b) => b.key === aiDlg.blokKey);
    const origineel = blok?.[aiDlg.veld] || '';
    if (!origineel.trim()) return;
    setAiDlg((d) => ({ ...d, bezig: true, resultaat: null }));
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/rewrite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: origineel, instruction: aiDlg.instructie || '' }),
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || 'Herschrijven mislukt');
      const data = await resp.json();
      setAiDlg((d) => (d ? { ...d, bezig: false, resultaat: data.text } : d));
    } catch (e) {
      toast.error(e.message);
      setAiDlg((d) => (d ? { ...d, bezig: false } : d));
    }
  };

  const downloadPdf = async () => {
    setPdfBezig(true);
    try {
      await flushSave(); // PDF altijd van de laatste stand
      const resp = await authFetch(`${BACKEND_URL}/Api/script-breakdown/${project.id}/script-pdf`);
      if (!resp.ok) throw new Error('PDF genereren mislukt');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Script - ${project.title}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
    finally { setPdfBezig(false); }
  };

  const handleGenerate = async () => {
    const platte = modus === 'blokken' ? naarPlatteTekst(blokken) : scriptText;
    if (!platte.trim()) { toast.error('Voer eerst een script in'); return; }
    setGenerating(true);
    try {
      await flushSave();
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${project.id}/generate-breakdown`, { method: 'POST' });
      if (!resp.ok) throw new Error((await resp.json()).detail || 'Generatie mislukt');
      const data = await resp.json();
      toast.success(`${data.scenes_created} scenes gegenereerd`);
      onRefresh(); onNext();
    } catch (e) { toast.error(e.message || 'Fout bij genereren'); }
    finally { setGenerating(false); }
  };

  const OPSLAG_LABEL = { bezig: 'Opslaan…', opgeslagen: '✓ Opgeslagen', fout: '⚠ Opslaan mislukt — wijziging blijft lokaal' };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Script schrijven</Typography>
        <ToggleButtonGroup size="small" exclusive value={modus} onChange={wisselModus}>
          <ToggleButton value="blokken" sx={{ textTransform: 'none', px: 1.5 }}>Scène-blokken</ToggleButton>
          <ToggleButton value="plat" sx={{ textTransform: 'none', px: 1.5 }}>Platte tekst</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {opslag !== 'idle' && (
          <Typography variant="caption" color={opslag === 'fout' ? 'error' : 'text.secondary'}>{OPSLAG_LABEL[opslag]}</Typography>
        )}
        <Button size="small" variant="outlined" startIcon={pdfBezig ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
          onClick={downloadPdf} disabled={pdfBezig} sx={{ textTransform: 'none' }}>
          Script-PDF
        </Button>
      </Stack>

      <TextField size="small" label="Projectnummer (koppeling Crew-app)" value={projectNumber}
        onChange={(e) => setProjectNumber(e.target.value)} sx={{ mb: 2, width: 260 }}
        placeholder="bv. 9678" />

      {modus === 'plat' ? (
        <TextField fullWidth multiline minRows={16} value={scriptText}
          onChange={(e) => setScriptText(e.target.value)}
          placeholder={'Plak of schrijf hier je script…\n\nTip: wissel naar Scène-blokken voor het twee-koloms formaat (voice-over naast beeld).'} />
      ) : (
        <Stack spacing={1.5}>
          {blokken.map((b, idx) => (
            <Paper key={b.key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Chip size="small" label={`Scène ${idx + 1}`} sx={{ fontWeight: 700 }} />
                <TextField size="small" variant="standard" placeholder="Titel (optioneel)" value={b.titel}
                  onChange={(e) => wijzigBlok(b.key, 'titel', e.target.value)} sx={{ flex: 1 }} />
                <Tooltip title="Omhoog"><span><IconButton size="small" disabled={idx === 0} onClick={() => verplaatsBlok(idx, -1)}><ArrowUpwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
                <Tooltip title="Omlaag"><span><IconButton size="small" disabled={idx === blokken.length - 1} onClick={() => verplaatsBlok(idx, 1)}><ArrowDownwardIcon fontSize="inherit" /></IconButton></span></Tooltip>
                <Tooltip title="Scène verwijderen"><span><IconButton size="small" disabled={blokken.length === 1} onClick={() => verwijderBlok(b.key)}><DeleteOutlineIcon fontSize="inherit" /></IconButton></span></Tooltip>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 1.5 }}>
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" fontWeight={700} color="text.secondary">VOICE-OVER / DIALOOG</Typography>
                    <Tooltip title="Herschrijf met AI">
                      <IconButton size="small" onClick={() => setAiDlg({ blokKey: b.key, veld: 'vo', instructie: '', resultaat: null, bezig: false })}>
                        <AutoFixHighIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <TextField fullWidth multiline minRows={3} size="small" value={b.vo}
                    onChange={(e) => wijzigBlok(b.key, 'vo', e.target.value)}
                    placeholder="Wat wordt er gezegd…" />
                </Box>
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: '5px' }}>BEELD</Typography>
                  <TextField fullWidth multiline minRows={3} size="small" value={b.beeld}
                    onChange={(e) => wijzigBlok(b.key, 'beeld', e.target.value)}
                    placeholder="Wat zien we…" />
                </Box>
              </Box>
            </Paper>
          ))}
          <Button startIcon={<AddIcon />} onClick={() => setBlokken((prev) => [...prev, leegBlok()])}
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
            Scène toevoegen
          </Button>
        </Stack>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button variant="contained" startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
          onClick={handleGenerate} disabled={generating} sx={{ textTransform: 'none' }}>
          {generating ? 'Bezig met genereren…' : 'Genereer breakdown →'}
        </Button>
      </Stack>

      {/* ✨ Herschrijf-dialoog: instructie → voorstel → overnemen */}
      <Dialog open={!!aiDlg} onClose={() => aiDlg?.bezig || setAiDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>✨ Herschrijf voice-over</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth size="small" label="Instructie (optioneel)" value={aiDlg?.instructie || ''}
            onChange={(e) => setAiDlg((d) => ({ ...d, instructie: e.target.value }))}
            placeholder="bv. korter en actiever, spreektaal, formeler…" sx={{ mb: 1.5, mt: 0.5 }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runHerschrijf(); } }} />
          {aiDlg?.bezig && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>}
          {aiDlg?.resultaat && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f8fafc', whiteSpace: 'pre-wrap' }}>
              <Typography variant="body2">{aiDlg.resultaat}</Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiDlg(null)} disabled={aiDlg?.bezig} sx={{ textTransform: 'none' }}>Sluiten</Button>
          <Button onClick={runHerschrijf} disabled={aiDlg?.bezig} sx={{ textTransform: 'none' }}>
            {aiDlg?.resultaat ? 'Opnieuw' : 'Herschrijf'}
          </Button>
          {aiDlg?.resultaat && (
            <Button variant="contained" sx={{ textTransform: 'none' }}
              onClick={() => { wijzigBlok(aiDlg.blokKey, aiDlg.veld, aiDlg.resultaat); setAiDlg(null); }}>
              Overnemen
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ScriptInput;
